import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@zentrani/ui";
import { NavIcon } from "./NavIcon";
import { moreNavIsActive, type NavItem } from "./navConfig";

function itemClass(active: boolean) {
  return cn(
    "relative flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-center touch-manipulation select-none",
    active ? "bg-brand-50 font-semibold text-brand-800" : "font-medium text-ink-soft hover:bg-slate-100"
  );
}

export function BottomNav({
  primary,
  more,
  scrollportRef,
}: {
  primary: NavItem[];
  more: NavItem[];
  scrollportRef?: RefObject<HTMLElement | null>;
}) {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const sheetId = useId();
  const moreActive = moreNavIsActive(location.pathname, more);

  function closeMore() {
    setMoreOpen(false);
    moreButtonRef.current?.focus();
  }

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const closeOnDesktop = () => {
      if (mq.matches) setMoreOpen(false);
    };
    mq.addEventListener("change", closeOnDesktop);
    return () => mq.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const scrollport = scrollportRef?.current;
    const previousBodyOverflow = document.body.style.overflow;
    const previousScrollOverflow = scrollport?.style.overflow ?? "";
    document.body.style.overflow = "hidden";
    if (scrollport) scrollport.style.overflow = "hidden";
    sheetRef.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setMoreOpen(false);
        moreButtonRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollport) scrollport.style.overflow = previousScrollOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen, scrollportRef]);

  return (
    <>
      {moreOpen ? (
        <button
          type="button"
          className="absolute inset-0 z-30 bg-ink/40 md:hidden"
          aria-label="Cerrar menú"
          onClick={closeMore}
        />
      ) : null}

      <div className="relative z-40 md:hidden">
        {moreOpen ? (
          <div
            ref={sheetRef}
            id={sheetId}
            role="dialog"
            aria-labelledby={`${sheetId}-title`}
            tabIndex={-1}
            className="absolute bottom-full inset-x-0 mx-auto w-full max-w-app px-3 pb-2 animate-toast-in"
          >
            <div className="overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-slate-900/5">
              <p id={`${sheetId}-title`} className="px-4 pb-2 pt-4 text-base font-semibold text-ink">
                Más secciones
              </p>
              <ul className="flex flex-col gap-1 p-2 pb-3">
                {more.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium",
                          isActive ? "bg-brand-50 text-brand-800" : "text-ink-soft hover:bg-slate-100"
                        )
                      }
                    >
                      <NavIcon name={item.icon} />
                      <span className="min-w-0 flex-1">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <nav
          className="select-none border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom,0px)]"
          aria-label="Secciones"
        >
          <div className="mx-auto flex w-full max-w-app gap-0.5 px-1.5 py-1">
            {primary.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                aria-label={item.shortLabel ? item.label : undefined}
                className={({ isActive }) => itemClass(isActive)}
              >
                <NavIcon name={item.icon} />
                <span className="line-clamp-2 w-full text-[11px] leading-tight">
                  {item.shortLabel ?? item.label}
                </span>
              </NavLink>
            ))}
            {more.length > 0 ? (
              <button
                ref={moreButtonRef}
                type="button"
                className={itemClass(moreOpen || moreActive)}
                aria-expanded={moreOpen}
                aria-controls={moreOpen ? sheetId : undefined}
                aria-current={moreActive ? "page" : undefined}
                onClick={() => setMoreOpen((open) => !open)}
              >
                <NavIcon name="more" />
                <span className="line-clamp-2 w-full text-[11px] leading-tight">Más</span>
              </button>
            ) : null}
          </div>
        </nav>
      </div>
    </>
  );
}
